<template>
  <container-item-wrapper :widget="widget">
    <van-collapse v-model="widget.options.expandName" :border="widget.options.border" :accordion="accordion">
      <van-collapse-item v-bind="item.options" v-for="(item, itemIdx) in widget.widgetList" :name="item.options.name" :key="itemIdx">
        <template v-for="(itemWidget, swIdx) in item.widgetList">
          <template v-if="'container' === itemWidget.category">
            <component
              :is="itemWidget.type + '-item'"
              :widget="itemWidget"
              :key="itemWidget.id"
              :parent-list="item.widgetList"
              :index-of-parent-list="swIdx"
              :parent-widget="widget"
              :sub-form-row-id="subFormRowId"
              :sub-form-row-index="subFormRowIndex"
              :sub-form-col-index="subFormColIndex"
            >
            </component>
          </template>
          <template v-else>
            <component
              :is="itemWidget.type + '-widget'"
              :field="itemWidget"
              :key="itemWidget.id"
              :parent-list="item.widgetList"
              :index-of-parent-list="swIdx"
              :parent-widget="widget"
              :design-state="false"
              :sub-form-row-id="subFormRowId"
              :sub-form-row-index="subFormRowIndex"
              :sub-form-col-index="subFormColIndex"
            ></component>
          </template>
        </template>
      </van-collapse-item>
    </van-collapse>
  </container-item-wrapper>
</template>

<script>
import { computed, toRefs, reactive, inject, onBeforeUnmount, nextTick, onMounted, getCurrentInstance } from 'vue';
import { useI18n } from '@/utils/i18n';
import { useEmitter } from '@/utils/emitter';
import { useRef } from '@/components/form-render/refMixin';
import { useContainer } from '@/components/form-render/container-item/containerItemMixin';
import FieldComponents from '@/components/form-designer/form-widget/field-widget/index';
import ContainerItemWrapper from '@/components/form-render/container-item/container-item-wrapper';
import { traverseFieldWidgets } from '@/utils/util';

export default {
  componentName: 'ContainerItem',
  components: {
    ContainerItemWrapper,
    ...FieldComponents,
  },
  name: 'collapse-item',
  props: {
    subFormColIndex: {
      default: -1,
      /* 子表单组件列索引，从0开始计数 */
      type: Number,
    },

    subFormRowId: {
      default: '',
      /* 子表单组件行Id，唯一id且不可变 */
      type: String,
    },
    subFormRowIndex: {
      default: -1,
      /* 子表单组件行索引，从0开始计数 */
      type: Number,
    },
    widget: Object,
  },

  setup(props) {
    const { i18nt } = useI18n();
    const refMixin = useRef();
    const containerMixin = useContainer();

    const refList = inject('refList');
    const sfRefList = inject('sfRefList');
    const globalModel = inject('globalModel');

    const data = reactive({
      activeNames: null,
    });

    const emitterMixin = useEmitter(data);

    const customClass = computed(() => {
      return props.widget.options.customClass || '';
    });

    const accordion = computed(() => {
      //手风琴模式accodition expandName是字符串，非手风琴模式是数组，需要根据设置自动改变
      //直接给组件的accodion属性赋值，expandName 来不及改变产生错误，因此加如这个计算属性，
      //反过来根据expandName变化来决定 accodion属性值
      return !Array.isArray(props.widget.options.expandName);
    });

    // 辅助函数：更新单个组件的状态
    const updateWidgetState = (widget, stateName, flag) => {
      const formRef = containerMixin.getFormRef();
      let stateNames = Array.isArray(stateName) ? stateName : [stateName];
      // 设置collapseItem的属性状态
      stateNames.forEach(state => {
        widget.options[state] = flag;
      });

      nextTick(() => {
        traverseFieldWidgets(widget.widgetList, item => {
          const widgetRef = containerMixin.getWidgetRef(item.options.name);
          stateNames.forEach(state => {
            let stateFuncName = 'set' + state.substring(0, 1).toUpperCase() + state.substring(1);
            widgetRef[stateFuncName](flag);
          });

          if (flag) {
            widgetRef.clearFieldRules();
          } else {
            widgetRef.buildFieldRules();
          }
        });
        formRef.clearValidate(widget.widgetList.map(item => item.options.name));
      });
    };

    // 设置折叠面板只读，同时让内部组件只读
    const setCollapseItemReadonly = (itemIndex = 0, flag = true) => {
      updateWidgetState(props.widget.widgetList[itemIndex], 'readonly', flag);
    };

    // 设置折叠面板禁用，同时禁用内部组件
    const setCollapseItemDisabled = (itemIndex = 0, flag = true) => {
      updateWidgetState(props.widget.widgetList[itemIndex], 'disabled', flag);
    };

    onBeforeUnmount(() => {
      containerMixin.unregisterFromRefList();
    });

    refMixin.initRefList();

    nextTick(() => {
      props.widget.widgetList.forEach((item, itemIdx) => {
        let updateState = [];
        if (item.options.readonly) updateState.push('readonly');
        if (item.options.disabled) updateState.push('disabled');
        if (updateState.length > 0) updateWidgetState(item, updateState, true);
      });
    });

    return {
      ...i18nt,
      ...toRefs(props),
      ...toRefs(data),
      ...emitterMixin,
      ...containerMixin,

      accordion,
      customClass,
      setCollapseItemDisabled,
      setCollapseItemReadonly,
    };
  },
};
</script>

<style lang="scss" scoped>
:deep(.el-card__header) {
  padding: 10px 12px;
}

.folded :deep(.el-card__body) {
  display: none;
}

.clear-fix:before,
.clear-fix:after {
  display: table;
  content: '';
}

.clear-fix:after {
  clear: both;
}

.float-right {
  float: right;
}
</style>
